/**
 * The gateway seam's MEMORY — what each tenant's calls have added up to, kept per workspace.
 *
 * Split out of ./gatewaySeam.ts during the PR #533 review round at the 500-LOC ratchet's
 * insistence (STD-2 / DEC-1504), and the ratchet was reading the design correctly: classifying
 * how one call failed and remembering what a tenant's calls have cost are different jobs with
 * different reasons to change. gatewaySeam.ts keeps the error taxonomy and the recording
 * orchestration; this module owns the stores and the invariant that makes them safe.
 *
 * THAT INVARIANT IS TENANT PARTITIONING. In HTTP transport one MCP process multiplexes many
 * tenants (../http.ts's per-request `runWithAuth`), so every structure here is keyed by
 * workspace, bounded per workspace, and readable only by naming the scopes you own. Both
 * stores share one LRU (`touchBoundedWorkspaceMap`) precisely so they can never disagree about
 * which tenants are retained — a buffer that evicted a workspace the counters kept would
 * report one tenant's rates beside another tenant's call list.
 *
 * ../gatewaySeam.ts re-exports this module's public surface, so ../client.ts and the test mocks
 * that enumerate client.js's exports keep working unchanged.
 */
// ─── Audit buffer ─────────────────────────────────────────────────────

export interface AuditEntry {
  ts: string;
  /**
   * Monotonic per-process recording order — the key `getAuditLog` merges a caller's buckets by.
   *
   * `ts` cannot do this job: it is ISO-8601 with MILLISECOND resolution, and several gateway
   * calls routinely land inside one millisecond, so sorting by it leaves same-ms entries in
   * whatever order the buckets happened to be visited. The old single global buffer got true
   * insertion order for free; partitioning it per workspace is what makes an explicit ordering
   * key necessary. Internal to the seam — the audit view renders `ts`, never this.
   */
  seq: number;
  fn: string;
  workspace: string;
  status: "ok" | "error";
  durationMs: number;
  error?: string;
  /** For compound tools: tool name and action for audit display */
  toolContext?: { tool: string; action?: string };
  /**
   * WP-575: the declared latency budget this call was given, in ms. Recorded on every entry
   * (not just failures) so an observed duration can be read against the budget it was
   * actually judged by, rather than against whatever the budget happens to be today.
   */
  budgetMs?: number;
  /** WP-575: true when this call failed by hitting `budgetMs`, not by a server rejection. */
  timedOut?: boolean;
}

/**
 * PER-WORKSPACE, not one global ring (PR #533 review, Codex P2).
 *
 * This buffer pre-dates the work package (it lived in client.ts) and was a single process-wide
 * array evicted with `shift()`. In HTTP transport one process multiplexes many tenants, and
 * `lib/auditView.ts` filters to the caller AFTER eviction has already happened — so any tenant
 * making 50 calls silently erased every other tenant's entries. The quiet tenant's
 * `health action=audit` then reported nothing at all while its own lifetime timeout counters
 * were nonzero: a noisy neighbour deleting exactly the OBJ-79 evidence this work package
 * exists to make readable.
 *
 * The cap is now PER WORKSPACE, so 50 is a floor on what each tenant can see rather than a
 * pool they compete for, and the map of workspaces is bounded by the same `MAX_SEAM_WORKSPACES`
 * LRU as the counters — same `touchBoundedWorkspaceMap` helper, so the two can never disagree
 * about which tenants are retained.
 */
const AUDIT_BUFFER_SIZE = 50;
const auditBufferByWorkspace = new Map<string, AuditEntry[]>();
/** Deliberately NOT reset by `__resetGatewaySeamCountersForTest` — order must stay strictly increasing for the life of the process, and nothing reads it as a count. */
let nextAuditSeq = 0;

/**
 * This caller's slice of the audit buffer, oldest first, merged across the scopes it owns.
 *
 * Takes scopes for the same reason `getMergedGatewaySeamCounters` does — a session's first
 * call is filed under `cacheScope()` before `resolveWorkspace` returns — and the two MUST
 * merge over the same scope set or the log and the counters would describe different callers.
 * There is deliberately no "give me everything" shape: that is what leaked across tenants.
 *
 * Merged by `seq`, the recording order — within one workspace that is exactly what the old
 * global buffer returned; across a caller's two scopes it interleaves them, which is what
 * "this caller's recent calls" means. See `AuditEntry.seq` for why not `ts`.
 */
export function getAuditLog(scopes: readonly string[]): readonly AuditEntry[] {
  const merged: AuditEntry[] = [];
  for (const scope of new Set(scopes)) {
    const bucket = auditBufferByWorkspace.get(scope);
    if (bucket) merged.push(...bucket);
  }
  return merged.sort((a, b) => a.seq - b.seq);
}

// ─── Rolling latency + error counters (WP-575 element 5) ──────────────

/**
 * WHY COUNTERS AND NOT JUST THE BUFFER: `auditBuffer` holds the last 50 entries, so it
 * answers "what happened recently" but cannot answer "what is the failure rate" — the older
 * entries are gone. OBJ-79 measures exactly that (batch success rate, chain.createEntry
 * false-failure rate), and until now the only way to report it was to self-grade. These
 * counters are cumulative for the process lifetime and never evict, so the rate is real.
 *
 * `timeouts` is tracked SEPARATELY from `errors` on purpose, and it is the number this work
 * package exists to drive to zero: a timeout is the class of failure where the server may
 * actually have succeeded, so lumping it in with genuine server rejections is what made the
 * false-failure rate unmeasurable in the first place. Note `timeouts` is a SUBSET of
 * `errors` — every timeout is also counted there, so `errors` remains "all failures."
 */
export interface GatewaySeamCounters {
  calls: number;
  errors: number;
  timeouts: number;
  /** Summed wall-clock ms across all calls — divide by `calls` for the mean. */
  totalDurationMs: number;
  /** Slowest single call observed, in ms. */
  maxDurationMs: number;
  /** Per-route breakdown, same fields, for attributing a rate to the route that caused it. */
  byRoute: Record<string, { calls: number; errors: number; timeouts: number; totalDurationMs: number; maxDurationMs: number }>;
}

/** A null-prototype `byRoute` — see `emptySeamCounters` for why it must never be a plain `{}`. */
function emptyByRoute(): GatewaySeamCounters["byRoute"] {
  return Object.create(null) as GatewaySeamCounters["byRoute"];
}

/**
 * `byRoute` is NULL-PROTOTYPE, deliberately (PR #533 review round 3, Copilot).
 *
 * It is indexed by an arbitrary route NAME at three sites — `recordSeamCounters`, the snapshot
 * below, and `getMergedGatewaySeamCounters` — each via `byRoute[fn] ??= {...}`. On a plain `{}`
 * a route named `toString`/`constructor`/`valueOf` resolves to the INHERITED function, which is
 * neither null nor undefined, so `??=` does not assign and the following `route.calls += 1`
 * writes onto a function and yields **NaN** — silently corrupting the very counters OBJ-79 is
 * measured from, with no error to notice. This is the same Object.prototype collision this PR
 * already closed for the route→type map; `byRoute` was the sibling left unswept.
 */
function emptySeamCounters(): GatewaySeamCounters {
  return { calls: 0, errors: 0, timeouts: 0, totalDurationMs: 0, maxDurationMs: 0, byRoute: emptyByRoute() };
}

/**
 * PR #533 review, Codex P2: in HTTP transport mode one process multiplexes MANY tenants
 * (see http.ts's per-request `runWithAuth`), so a single module-scope counters object — what
 * this used to be — let any authenticated caller read every OTHER tenant's process-lifetime
 * call/error/timeout history through `workspace action=audit`. Keying by workspace makes the
 * isolation structural: `getGatewaySeamCounters` can only ever return the slice for the
 * workspace it's asked for, so a caller who doesn't know another tenant's workspaceId cannot
 * reach that tenant's counters. In stdio mode there is exactly one workspace, so this degrades
 * to the old single-bucket behavior with no observable change.
 *
 * BOUNDED MAP: an HTTP process can be handed at most `MAX_SESSIONS` (200, http.ts) concurrent
 * sessions, and each session belongs to one workspace — so 200 is also the ceiling on distinct
 * workspaces a single process can plausibly be serving at once. Cap at that same number so a
 * long-lived multi-tenant process can't grow this map without limit.
 *
 * EVICT LEAST-RECENTLY-RECORDED, not oldest-inserted. This started as insertion-order FIFO
 * borrowed from `_sessionLifecycleByStream`, and that precedent does not transfer: its keys are
 * write-once per stream, so FIFO and LRU coincide there. A `workspaceId` is a STABLE key reused
 * for the life of the process, so under FIFO the workspace that has been active LONGEST is the
 * first evicted — the single most active tenant loses its counters the moment workspace #201
 * appears, and its next `workspace action=audit` reports zeros as if it had never made a call.
 * That silently defeats the OBJ-79 observability this fix exists to provide. Recording a call
 * re-inserts the key so it moves to the end of the Map's insertion order, making eviction
 * genuinely least-recently-used — the same intent as http.ts's `lastAccess` sort, without
 * carrying a timestamp per bucket.
 */
const MAX_SEAM_WORKSPACES = 200;
const seamCountersByWorkspace = new Map<string, GatewaySeamCounters>();

/**
 * Fetch-or-create a workspace's bucket, refreshing its LRU position and evicting the
 * least-recently-recorded workspace once the map is full.
 *
 * Shared by the counters and the audit buffer rather than written twice: both are keyed by
 * workspace, both are bounded by the same ceiling, and both must retain the same tenants — a
 * buffer that evicted a workspace the counters kept (or vice versa) would report a tenant's
 * rates against another tenant's call list. The eviction reasoning is in the comment above.
 */
function touchBoundedWorkspaceMap<V>(map: Map<string, V>, workspace: string, create: () => V): V {
  const existing = map.get(workspace);
  if (existing !== undefined) {
    // Move to the end so eviction below is least-recently-RECORDED, not oldest-inserted.
    // A Map re-`set` of an existing key keeps the value and refreshes its insertion position.
    map.delete(workspace);
    map.set(workspace, existing);
    return existing;
  }
  if (map.size >= MAX_SEAM_WORKSPACES) {
    const leastRecentlyRecorded = map.keys().next().value;
    if (leastRecentlyRecorded !== undefined) map.delete(leastRecentlyRecorded);
  }
  const created = create();
  map.set(workspace, created);
  return created;
}

function getOrCreateWorkspaceCounters(workspace: string): GatewaySeamCounters {
  return touchBoundedWorkspaceMap(seamCountersByWorkspace, workspace, emptySeamCounters);
}

function getOrCreateWorkspaceAuditBuffer(workspace: string): AuditEntry[] {
  return touchBoundedWorkspaceMap(auditBufferByWorkspace, workspace, () => []);
}

/**
 * Snapshot of ONE workspace's gateway seam counters — deep-copied so a caller can hold it
 * across further calls without it mutating underneath them.
 *
 * PR #533 review, Codex P2: `workspace` is required, not optional — there is no "give me
 * everything" call shape, because that shape is exactly what let one tenant read another's
 * history. A workspace with no recorded calls yet gets a fresh empty snapshot, never another
 * workspace's data and never the old global aggregate.
 */
export function getGatewaySeamCounters(workspace: string): GatewaySeamCounters {
  const counters = seamCountersByWorkspace.get(workspace);
  if (!counters) return emptySeamCounters();
  return {
    calls: counters.calls,
    errors: counters.errors,
    timeouts: counters.timeouts,
    totalDurationMs: counters.totalDurationMs,
    maxDurationMs: counters.maxDurationMs,
    // Copied onto a null prototype, not left as `Object.fromEntries`' plain object — the
    // snapshot is indexed by route name downstream (`getMergedGatewaySeamCounters`) and would
    // reintroduce the inherited-member collision `emptySeamCounters` documents.
    byRoute: Object.assign(emptyByRoute(), Object.fromEntries(Object.entries(counters.byRoute).map(([k, v]) => [k, { ...v }]))),
  };
}

/**
 * Snapshot merged across SEVERAL buckets belonging to the SAME caller.
 *
 * Exists because a session's calls are not all recorded under one key. `audit()` attributes a
 * call to `state().workspaceId ?? cacheScope()` (client.ts), and `workspaceId` is only set once
 * `resolveWorkspace` RETURNS — so every session's first call, `resolveWorkspace` itself, is
 * recorded under `cacheScope()`. Filtering the audit view to the resolved workspaceId alone
 * therefore hid that call forever, under-counting every tenant's own view of itself (including
 * in stdio mode, where the pre-fix global aggregate had shown it).
 *
 * Merging is safe precisely because `cacheScope()` is derived from the CALLER'S OWN API key —
 * both buckets are the same tenant, so this widens a caller's view of itself without ever
 * reaching another tenant's. Callers must only pass scopes they have proven belong to them.
 */
export function getMergedGatewaySeamCounters(scopes: readonly string[]): GatewaySeamCounters {
  const merged = emptySeamCounters();
  for (const scope of new Set(scopes)) {
    const part = getGatewaySeamCounters(scope);
    merged.calls += part.calls;
    merged.errors += part.errors;
    merged.timeouts += part.timeouts;
    merged.totalDurationMs += part.totalDurationMs;
    merged.maxDurationMs = Math.max(merged.maxDurationMs, part.maxDurationMs);
    for (const [route, r] of Object.entries(part.byRoute)) {
      const into = (merged.byRoute[route] ??= { calls: 0, errors: 0, timeouts: 0, totalDurationMs: 0, maxDurationMs: 0 });
      into.calls += r.calls;
      into.errors += r.errors;
      into.timeouts += r.timeouts;
      into.totalDurationMs += r.totalDurationMs;
      into.maxDurationMs = Math.max(into.maxDurationMs, r.maxDurationMs);
    }
  }
  return merged;
}

/**
 * Test-only reset — the counters are process-lifetime cumulative by design.
 *
 * Clears the audit buffer too: both are per-workspace seam state written by the same
 * `recordGatewayCall`, so resetting one and leaving the other would let a test's assertions
 * about "this tenant's calls" read entries from the previous test.
 */
export function __resetGatewaySeamCountersForTest(): void {
  seamCountersByWorkspace.clear();
  auditBufferByWorkspace.clear();
}

/**
 * Append one recorded call to its workspace's buffer, stamping the ordering `seq`.
 *
 * The seq stamp lives here, with the store, rather than at the caller: it is the store's
 * ordering key and nothing outside should be able to mint one out of order.
 */
export function appendAuditEntry(entry: Omit<AuditEntry, "seq">): void {
  const stored: AuditEntry = { ...entry, seq: nextAuditSeq++ };
  const bucket = getOrCreateWorkspaceAuditBuffer(stored.workspace);
  bucket.push(stored);
  if (bucket.length > AUDIT_BUFFER_SIZE) bucket.shift();
}

export function recordSeamCounters(
  workspace: string,
  fn: string,
  status: "ok" | "error",
  durationMs: number,
  timedOut: boolean,
): void {
  const seamCounters = getOrCreateWorkspaceCounters(workspace);
  const route = (seamCounters.byRoute[fn] ??= {
    calls: 0, errors: 0, timeouts: 0, totalDurationMs: 0, maxDurationMs: 0,
  });

  seamCounters.calls += 1;
  seamCounters.totalDurationMs += durationMs;
  if (durationMs > seamCounters.maxDurationMs) seamCounters.maxDurationMs = durationMs;
  route.calls += 1;
  route.totalDurationMs += durationMs;
  if (durationMs > route.maxDurationMs) route.maxDurationMs = durationMs;

  if (status === "error") {
    seamCounters.errors += 1;
    route.errors += 1;
    if (timedOut) {
      seamCounters.timeouts += 1;
      route.timeouts += 1;
    }
  }
}
/**
 * Human-readable seam summary for `health action=audit` (WP-575 element 5).
 *
 * Lives here, with the counters, rather than in the tool: the tool renders an audit view,
 * this module decides what the numbers MEAN — notably that a timeout gets its own line
 * instead of being folded into the error rate, because it is the failure class where the
 * server may actually have SUCCEEDED.
 */
export function formatGatewaySeamSummary(seam: GatewaySeamCounters): string {
  const lines = [
    "\n\n---\n\n# Gateway seam (process lifetime)\n",
    `Calls: ${seam.calls} \u2014 errors: ${seam.errors}, of which timeouts: ${seam.timeouts}`,
  ];
  if (seam.calls > 0) {
    lines.push(`Mean: ${Math.round(seam.totalDurationMs / seam.calls)}ms \u2014 slowest: ${seam.maxDurationMs}ms`);
  }
  if (seam.timeouts > 0) {
    lines.push(`\u26a0 ${seam.timeouts} call(s) hit their latency budget \u2014 those outcomes are unknown, not failed.`);
  }
  return lines.join("\n");
}
